import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Shield, BookOpen, Target, Sparkles, TrendingUp } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function TextMeiAlternativePage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'TextMei Alternative - VZMO AI Life Coach | Free Alternative to TextMei';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Looking for a TextMei alternative? VZMO offers AI life coaching with privacy-first journaling, structured action plans, and career guidance. Free 7-day trial.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/compare/textmei-alternative');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/compare/textmei-alternative';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('textmei-comparison');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Looking for a TextMei Alternative?
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            VZMO offers everything TextMei does, plus structured action plans, deeper career guidance,
            and privacy-first journaling. Get 7 days free.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">VZMO</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">TextMei</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">AI Life Coaching</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">24/7 Availability</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Structured Journaling</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Action Plans & Accountability</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Career Clarity Tools</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Weekly Progress Reports</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Relationship Coaching</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Export Your Data Anytime</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><span className="text-xs text-slate-500">Limited</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Starting Price</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">$7/month</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">$10/month</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Free Trial</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">7 days</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">3 days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <Shield className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Privacy-First Design</h3>
            <p className="text-slate-600">
              Your journal entries and coaching conversations are encrypted end-to-end.
              We never use your data to train AI models or share with third parties.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <Target className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Structured Action Plans</h3>
            <p className="text-slate-600">
              Unlike simple chat interfaces, VZMO creates actionable plans with milestones,
              deadlines, and accountability check-ins to ensure you make real progress.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <BookOpen className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Career Clarity Tools</h3>
            <p className="text-slate-600">
              Specialized prompts and frameworks for career decisions, skill development,
              and professional growth that go beyond general life coaching.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <TrendingUp className="w-12 h-12 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Progress Tracking</h3>
            <p className="text-slate-600">
              Weekly scorecards and visual progress reports help you see your growth over time
              and identify patterns in your thinking and behavior.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Sparkles className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Ready to Try VZMO?</h2>
          <p className="text-lg mb-6 opacity-90">
            Start your 7-day free trial. No credit card required.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                How is VZMO different from TextMei?
              </h3>
              <p className="text-slate-600">
                While both offer AI life coaching, VZMO provides structured journaling, actionable plans with
                accountability tracking, specialized career clarity tools, and weekly progress reports. VZMO
                is designed for people who want more than conversation - they want structured guidance and measurable progress.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Can I import my TextMei conversations to VZMO?
              </h3>
              <p className="text-slate-600">
                While we can't directly import from TextMei, you can manually copy important insights into your
                VZMO journal. Our AI coach can review past experiences and continue your growth journey seamlessly.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is VZMO more expensive than TextMei?
              </h3>
              <p className="text-slate-600">
                No! VZMO starts at $7/month compared to TextMei's $10/month. Plus, we offer a longer free trial
                (7 days vs 3 days) so you can fully experience the platform before committing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What happens to my data if I switch from TextMei?
              </h3>
              <p className="text-slate-600">
                Your VZMO data is completely separate and private. We use end-to-end encryption and never share
                your information. You can export all your data anytime and delete your account with one click.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
